import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaSyncAlt } from 'react-icons/fa';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import swal from 'sweetalert'; // Import sweetalert
import { useRechargeOptions } from './RechargeOptionsContext';
import Loading from './Loading'; // Assuming you have a Loading component
import { isMobile } from './platform';
import { Plugins } from '@capacitor/core';

const { Checkout } = Plugins;

const Wallet = ({ show, handleClose }) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [razorpaySettings, setRazorpaySettings] = useState(null);
    const { rechargeOptions, loading, error, walletBalance, fetchWalletBalance, startBalanceCheck } = useRechargeOptions();
    const user = JSON.parse(localStorage.getItem('user'));
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const [isRefreshing, setIsRefreshing] = useState(false); // State for refresh icon animation
    const [isLoading, setIsLoading] = useState(false); // State for loader before payment gateway

    useEffect(() => {
        const fetchRazorpaySettings = async () => {
            try {
                const response = await axios.get(`${apiUrl}/razorpay-settings`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setRazorpaySettings(response.data);
            } catch (error) {
                console.error('Error fetching Razorpay settings:', error);
            }
        };

        fetchRazorpaySettings();
    }, [apiUrl, token]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchWalletBalance(); // Manually fetch the wallet balance when refresh is clicked
        setIsRefreshing(false);
    };

    if (loading) {  // Show loader during page load or recharge process
        return <Loading loadingText="Consulting the cosmos" />;
    }
    if (isLoading) {
        return <Loading loadingText="Hang tight! We're securely connecting you to our payment partner..." />;
    }

    if (error) {
        return <div></div>;
    }

    const handleRecharge = async (voucher) => {
        if (!razorpaySettings) {
            console.error('Razorpay settings not loaded.');
            return;
        }

        setIsLoading(true); // Start loader before payment processing

        // Fetch order from the backend
        const orderResponse = await axios.post(`${apiUrl}/create-order`, {
            voucher_id: voucher.id,
            amount: voucher.coupon_price,
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        });

        startBalanceCheck(walletBalance);
        const { order_id } = orderResponse.data;

        const options = {
            key: razorpaySettings.key,
            amount: voucher.coupon_price * 100, // Razorpay expects the amount in paise
            currency: razorpaySettings.currency_type,
            name: 'Astroyami',
            description: `Recharge with Rs ${voucher.coupon_price}`,
            image: razorpaySettings.image_url, // Use URL from settings
            order_id: order_id, // Include the order_id here
            prefill: {
                name: user.name, // Replace with user's name
                email: user.email, // Replace with user's email
                contact: user.phone, // Replace with user's phone number
            },
            theme: {
                color: razorpaySettings.theme_color, // Use color from settings
            },
            handler: (response) => {
                setIsLoading(false); // Stop loader once payment is successful
            },
            modal: {
                ondismiss: () => {
                    console.log('Transaction was cancelled.');
                    setIsLoading(false); // Stop loader if transaction is cancelled
                },
            },
        };

        if (isMobile()) {
            try {
                const response = await Checkout.open(options);
                setIsLoading(false); // Stop loader after opening payment gateway
                swal({
                    title: "Recharge Successful!",
                    text: `Recharge of Rs ${voucher.coupon_price} was successful. Your wallet will update shortly. Sometimes, it may take a few minutes due to banking delays. Thank you for your patience!`,
                    icon: "success",
                    button: "OK",
                });
                handleClose();
            } catch (error) {
                setIsLoading(false); // Stop loader if payment fails
                handleClose();
            }
        } else {
            const rzp = new window.Razorpay(options);
            rzp.open();
            setIsLoading(false); // Stop loader after opening Razorpay modal
        }
    };

    const renderRechargeButton = (voucher) => {
        const totalDiscount = voucher.recharge_balance - voucher.coupon_price;
        const discountPercentage = ((totalDiscount / voucher.coupon_price) * 100).toFixed(0);

        return (
            <div
                key={voucher.id}
                className={`recharge-button ${selectedPlan?.id === voucher.id ? 'selected' : ''}`}
                onClick={() => {
                    setSelectedPlan(voucher);
                    handleRecharge(voucher); // Trigger payment process
                }}
            >
                <FaRupeeSign /> {voucher.coupon_price}
                {totalDiscount > 0 && (
                    <span className="featured-tag">{discountPercentage}% Extra</span>
                )}
                <p>{voucher.display_text}</p>
            </div>
        );
    };

    return (
        <div className={`wallet-overlay ${show ? 'show' : ''}`}>
            <div className="wallet-modal">
                <div className="wallet-header">
                    <IconButton className="arrow-left" onClick={handleClose} aria-label="Back">
                        <NavigateBeforeOutlinedIcon />
                    </IconButton>
                    <h5 className="wallet-title">Wallet Balance</h5>
                </div>
                <div className="wallet-body">
                    <div className="current-balance">
                        <div className="balance">
                            <span><FaRupeeSign />{walletBalance}</span> in your wallet
                            <FaSyncAlt 
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`} 
                                onClick={handleRefresh}
                                style={{ cursor: 'pointer', marginLeft: '10px' }}
                                title="Refresh Balance"
                            />
                        </div>
                    </div>
                    <div className="plans-section mt-4">
                        <div className="heading-center mb-4">
                            <h3><span>Recharge Now</span></h3>
                        </div>
                        <div className="recharge-buttons">
                            {rechargeOptions.map((voucher) => renderRechargeButton(voucher))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Wallet;
